// the whole open entry (headline/toggle area + text) may never exceed this
const ENTRY_MAX_HEIGHT = 275;
const ENTRY_MAX_HEIGHT_MOBILE = 760;
// matches .is-active .abx-lexikon__card-text-outer's padding-bottom in
// AbxLexikon.scss — kept outside the scrollable text so it's always
// visible, not just reachable by scrolling all the way to the end
const TEXT_BOTTOM_PADDING = 45;
const TEXT_BOTTOM_PADDING_MOBILE = 48;

/**
 * ABX-Lexikon accordion: one entry is "active" (expanded) at a time, the
 * rest stay collapsed as pills. Classic accordion behavior — entries never
 * change position; whichever one is opened just expands in place, and
 * closes back into a pill when another one is opened.
 *
 * Animations are disabled for now (see AbxLexikon.scss) — open/close and
 * the prev/next steps apply instantly, no transition to sequence around.
 *
 * @return {void}
 */
export default function InitAbxLexikon() {
    document.querySelectorAll('.abx-lexikon__panel').forEach(function (panel) {
        const entries = Array.from(panel.querySelectorAll('[data-abx-lexikon-entry]'));
        if (!entries.length) return;

        const module = panel.closest('.abx-lexikon');
        // matches $breakpointMedium in AbxLexikon.scss — entries render as
        // always-visible pills from here up, so opening one animates its
        // text height in place instead of just swapping which entry is shown
        const usesFixedModuleHeight = () => window.matchMedia('(min-width: 768px)').matches;

        // Pins the module's own box height to the tallest it has ever
        // needed, so it never SHRINKS as different entries are opened —
        // otherwise background-size: cover on this same element (see
        // AbxLexikon.scss) would visibly "zoom" as its box grows/shrinks.
        // A ResizeObserver (rather than measuring once right after an
        // open() call) sidesteps the max-height transition's own timing:
        // reading the box's height synchronously right after changing a
        // transitioned property can catch it mid-animation, not at its
        // settled target. The observer only ever ratchets the floor UP to
        // match real (already-rendered) content, so it can't force the box
        // bigger than it would otherwise need to be.
        let lockedHeight = 0;
        let heightObserver = null;

        const applyHeightLock = () => {
            if (!module) return;
            if (!usesFixedModuleHeight()) {
                heightObserver?.disconnect();
                heightObserver = null;
                lockedHeight = 0;
                module.style.removeProperty('min-height');
                return;
            }
            if (heightObserver) return;
            heightObserver = new ResizeObserver(() => {
                const natural = module.offsetHeight;
                if (natural > lockedHeight) {
                    lockedHeight = natural;
                    module.style.minHeight = lockedHeight + 'px';
                }
            });
            heightObserver.observe(module);
        };

        panel.classList.toggle('abx-lexikon__panel--single-entry', entries.length <= 1);

        const getEntryMaxHeight = () => (
            window.matchMedia('(max-width: 767px)').matches ? ENTRY_MAX_HEIGHT_MOBILE : ENTRY_MAX_HEIGHT
        );

        const getTextBottomPadding = () => (
            window.matchMedia('(max-width: 767px)').matches ? TEXT_BOTTOM_PADDING_MOBILE : TEXT_BOTTOM_PADDING
        );

        const getTextBudget = (toggle) => {
            const bottomPadding = getTextBottomPadding();
            const available = Math.max(0, getEntryMaxHeight() - toggle.offsetHeight);
            return {
                bottomPadding,
                textBudget: Math.max(0, available - bottomPadding),
            };
        };

        const measureTallestEntry = () => {
            panel.style.removeProperty('--abx-lexikon-active-entry-height');

            const tallest = entries.reduce((maxHeight, entry) => {
                const wasActive = entry.classList.contains('is-active');
                const outer = entry.querySelector('.abx-lexikon__card-text-outer');
                const inner = entry.querySelector('.abx-lexikon__card-text');
                const toggle = entry.querySelector('.abx-lexikon__toggle');
                const previousOuterMaxHeight = outer?.style.maxHeight || '';
                const previousInnerMaxHeight = inner?.style.maxHeight || '';

                entry.classList.add('is-active');

                if (toggle && outer && inner) {
                    const { bottomPadding, textBudget } = getTextBudget(toggle);
                    inner.style.maxHeight = textBudget + 'px';
                    outer.style.maxHeight = (Math.min(inner.scrollHeight, textBudget) + bottomPadding) + 'px';
                }

                const measuredHeight = entry.offsetHeight;

                if (!wasActive) {
                    entry.classList.remove('is-active');
                    if (outer) outer.style.maxHeight = previousOuterMaxHeight;
                    if (inner) inner.style.maxHeight = previousInnerMaxHeight;
                }

                return Math.max(maxHeight, measuredHeight);
            }, 0);

            panel.style.setProperty('--abx-lexikon-active-entry-height', Math.ceil(tallest) + 'px');
        };

        const open = (target) => {
            target.classList.add('is-active');
            target.querySelector('[data-action="abxLexikonToggle"]')?.setAttribute('aria-expanded', 'true');

            const toggle = target.querySelector('.abx-lexikon__toggle');
            const outer = target.querySelector('.abx-lexikon__card-text-outer');
            const inner = target.querySelector('.abx-lexikon__card-text');
            if (toggle && outer && inner) {
                const { bottomPadding, textBudget } = getTextBudget(toggle);
                inner.style.maxHeight = textBudget + 'px';
                // offsetHeight (not scrollHeight!) reflects the actually
                // rendered, possibly-capped-and-scrollable box
                const visibleTextHeight = Math.min(inner.offsetHeight, textBudget);
                outer.style.maxHeight = (visibleTextHeight + bottomPadding) + 'px';
            }
        };

        const close = (entry) => {
            const outer = entry.querySelector('.abx-lexikon__card-text-outer');
            if (outer) outer.style.maxHeight = '0px';
            entry.classList.remove('is-active');
            entry.querySelector('[data-action="abxLexikonToggle"]')?.setAttribute('aria-expanded', 'false');
        };

        const setActive = (target) => {
            const current = entries.find((entry) => entry.classList.contains('is-active'));
            if (current) close(current);
            open(target);
        };

        entries.forEach(function (entry) {
            entry.querySelector('[data-action="abxLexikonToggle"]')?.addEventListener('click', function () {
                if (!entry.classList.contains('is-active')) {
                    setActive(entry);
                }
            });
        });

        const step = (direction) => {
            const currentIndex = entries.findIndex((entry) => entry.classList.contains('is-active'));
            const nextIndex = (currentIndex + direction + entries.length) % entries.length;
            setActive(entries[nextIndex]);
        };

        panel.querySelector('[data-action="abxLexikonPrev"]')?.addEventListener('click', () => step(-1));
        panel.querySelector('[data-action="abxLexikonNext"]')?.addEventListener('click', () => step(1));

        // the first entry is already rendered with .is-active server-side —
        // give it its measured max-height too, since that now lives in JS
        const initiallyActive = entries.find((entry) => entry.classList.contains('is-active'));
        if (initiallyActive) {
            measureTallestEntry();
            open(initiallyActive);
            applyHeightLock();

            // Inter loads with font-display:swap, so this early measurement
            // can happen against the fallback font's (usually shorter) metrics.
            // Once the real font swaps in, the headline can grow taller than
            // what was budgeted, pushing the entry over its 275px ceiling.
            // Re-measure once the webfont is actually ready. (The height
            // lock's ResizeObserver picks up any resulting size change on
            // its own — no need to re-apply it here.)
            if (document.fonts && document.fonts.ready) {
                document.fonts.ready.then(function () {
                    if (initiallyActive.classList.contains('is-active')) {
                        measureTallestEntry();
                        open(initiallyActive);
                    }
                });
            }
        }

        window.addEventListener('resize', function () {
            const active = entries.find((entry) => entry.classList.contains('is-active'));
            measureTallestEntry();
            if (active) open(active);
            // re-measure the floor from scratch for the new viewport size
            // instead of carrying over a (possibly too-tall) old one
            lockedHeight = 0;
            module?.style.removeProperty('min-height');
            applyHeightLock();
        });
    });
}
